import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Droplet, Users, Clock, AlertTriangle } from 'lucide-react';

const content = {
  en: {
    eyebrow: 'THE WATER CRISIS',
    headline: 'A World in Need',
    subheadline: 'These are not just statistics. They represent mothers, children, and entire communities fighting for survival.',
    stats: [
      {
        value: 2.2,
        suffix: 'B',
        label: 'People without safe water',
        source: 'WHO/UNICEF 2023',
        icon: Users,
      },
      {
        value: 785,
        suffix: 'M',
        label: 'Lack basic water service',
        source: 'UN Water 2023',
        icon: Droplet,
      },
      {
        value: 2,
        suffix: 'M',
        label: 'Children die yearly',
        source: 'World Health Organization',
        icon: AlertTriangle,
      },
      {
        value: 4,
        suffix: 'hrs',
        label: 'Daily collection time',
        source: 'UN Women 2023',
        icon: Clock,
      },
    ],
    urgency: 'By 2050, half the world will face water scarcity.',
    cta: 'Help Now',
  },
  es: {
    eyebrow: 'LA CRISIS DEL AGUA',
    headline: 'Un Mundo en Necesidad',
    subheadline: 'Estas no son solo estadísticas. Representan madres, niños y comunidades enteras luchando por sobrevivir.',
    stats: [
      {
        value: 2.2,
        suffix: 'B',
        label: 'Personas sin agua segura',
        source: 'OMS/UNICEF 2023',
        icon: Users,
      },
      {
        value: 785,
        suffix: 'M',
        label: 'Sin servicio básico de agua',
        source: 'ONU Agua 2023',
        icon: Droplet,
      },
      {
        value: 2,
        suffix: 'M',
        label: 'Niños mueren al año',
        source: 'Organización Mundial de la Salud',
        icon: AlertTriangle,
      },
      {
        value: 4,
        suffix: 'hrs',
        label: 'Tiempo diario de recolección',
        source: 'ONU Mujeres 2023',
        icon: Clock,
      },
    ],
    urgency: 'Para 2050, la mitad del mundo enfrentará escasez de agua.',
    cta: 'Ayuda Ahora',
  },
};

const AnimatedCounter = ({ value, suffix, inView }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!inView) return;
    
    let start = 0;
    const duration = 2500;
    const increment = value / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [value, inView]);

  return (
    <span>
      {value >= 100 ? Math.round(count) : count.toFixed(1)}
      <span className="text-2xl md:text-3xl ml-1">{suffix}</span>
    </span>
  );
};

export const CrisisStats = ({ language }) => {
  const t = content[language];
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
  // Parallax
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  const scrollToForm = () => {
    const form = document.getElementById('donorbox-form');
    if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section 
      id="crisis" 
      ref={ref}
      className="relative py-32 overflow-hidden"
      data-testid="crisis-section"
    >
      {/* Seamless gradient background - NO CUTS */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F3A] via-[#071428] to-[#0A1F3A]" />
      
      {/* Parallax decorative element */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 opacity-5"
      >
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-cyan-500 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-amber-500 blur-3xl" />
      </motion.div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-amber-300/80 text-xs font-medium tracking-widest mb-6">
            {t.eyebrow}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white/90 mb-4">
            {t.headline}
          </h2>
          <p className="text-lg text-white/50 max-w-2xl mx-auto font-light">
            {t.subheadline}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {t.stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
                data-testid={`stat-card-${index}`}
              >
                <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 h-full hover:border-amber-500/30 transition-all duration-500">
                  <Icon className="w-8 h-8 text-amber-400/70 mb-4" />
                  
                  <div className="text-3xl md:text-4xl font-light text-white mb-2">
                    <AnimatedCounter 
                      value={stat.value} 
                      suffix={stat.suffix} 
                      inView={isInView}
                    />
                  </div>
                  
                  <p className="text-sm text-white/60 font-light mb-2">{stat.label}</p>
                  <p className="text-[10px] text-white/30 font-mono tracking-wider">{stat.source}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Urgency Banner - Subtle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-xl md:text-2xl font-light text-white/70 mb-6">
            {t.urgency}
          </p>
          <motion.button
            onClick={scrollToForm}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="px-8 py-3 rounded-full bg-gradient-to-r from-rose-500 via-pink-500 to-cyan-500 text-white font-semibold shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 transition-all"
          >
            {t.cta}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default CrisisStats;
